'use client';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode } from 'react';

export type ClarityNoticeSeverity = 'info' | 'danger';

const DEFAULT_ICONS: Record<ClarityNoticeSeverity, ReactElement> = {
  info: <InfoOutlinedIcon fontSize="small" />,
  danger: <ErrorOutlineIcon fontSize="small" />,
};

export type ClarityNoticeProps = {
  severity: ClarityNoticeSeverity;
  title?: string;
  children: ReactNode;
  icon?: ReactNode;
  'data-testid'?: string;
};

function ClarityNoticeComponent({
  severity,
  title,
  children,
  icon,
  'data-testid': dataTestId,
}: Readonly<ClarityNoticeProps>): ReactElement {
  const resolvedIcon = icon ?? DEFAULT_ICONS[severity];

  return (
    <Box
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.semantic.spacing.sm,
        padding: theme.semantic.spacing.base,
        borderRadius: theme.semantic.borderRadius.sm,
        backgroundColor: theme.notice[severity].background,
        color: theme.notice[severity].foreground,
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>{resolvedIcon}</Box>
      <Stack sx={(theme: Theme) => ({ gap: theme.semantic.spacing.xs })}>
        {title && (
          <Typography variant="h6" color="inherit">
            {title}
          </Typography>
        )}
        {typeof children === 'string' ? <Typography>{children}</Typography> : children}
      </Stack>
    </Box>
  );
}

export const ClarityNotice = withClarityTheme(ClarityNoticeComponent);
